import { useFormState } from 'informed';
import { useAchievementTypes, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { ProductIdentifier } from 'components/common';
import {
  Checkbox,
  FormGroup,
  FormSection,
  NumberStepper,
  RichText,
  Select,
  Text
} from 'components/common/form';
import { AchievementSettingsDetailsExternal } from '.';

import type { GetCategoriesRes } from 'shared-types/responses';

interface Props {
  validateTitle: (value: string) => Promise<string | undefined>;
}

const AchievementSettingsDetails = ({ validateTitle }: Props) => {
  const {
    messages,
    pageState: { achievement },
    serverConfig
  } = useReduxState();

  const formState = useFormState();

  const { data } = useApiData<GetCategoriesRes>({
    errorMessage: messages.categoriesGetCategoriesError,
    url: '/admin/categories-achievements'
  });

  const { supportClearingItems } = useAchievementTypes({
    type: achievement?.type
  });

  return (
    <>
      <FormSection maxWidth="100%">
        <FormGroup
          htmlFor="title"
          label={messages.achievementTitle}
          labelHelpTextServerConfigKey="achievementTitle"
        >
          <Text customValidateAsync={validateTitle} field="title" id="title" />
        </FormGroup>

        <ProductIdentifier productIdentifier={achievement.productIdentifier} />

        <FormGroup
          htmlFor="description"
          label={messages.commonDescription}
          labelHelpTextServerConfigKey="achievementDescription"
        >
          <RichText
            autoGrow={false}
            field="description"
            id="description"
            maxLength={3000}
          />
        </FormGroup>

        <FormGroup htmlFor="categories" label={messages.commonCategories}>
          <Select
            creatable={true}
            field="categories"
            id="categories"
            isClearable={true}
            isMulti={true}
            isSearchable={true}
            noOptionsMessage={messages.settingsNoCategoriesFound}
            options={
              data?.categories?.map((category) => ({
                label: category.title,
                value: category.title
              })) || []
            }
            placeholder={messages.settingsAddCategory}
          />
        </FormGroup>

        <AchievementSettingsDetailsExternal />
      </FormSection>

      {achievement.type === 'group-conversation' ||
      serverConfig.hideCalendarEventsFromUI ? null : (
        <FormSection
          helpTextServerConfigKey="achievementCalendarEvents"
          title={messages.commonCalendarEvents}
        >
          <Checkbox
            id="allowAnyoneLinkingToCalendarEvents"
            field="allowAnyoneLinkingToCalendarEvents"
            label={messages.achievementAllowCalendarEventLinking}
            labelTooltip={messages.achievementAllowCalendarEventLinkingInfo}
          />
        </FormSection>
      )}

      {supportClearingItems ? (
        <FormSection
          title={messages.achievementClearCompletionItems}
          tooltip={messages.achievementClearCompletionItemsTooltip}
        >
          <Checkbox
            id="enableAutomaticDeletionOfCompletionItems"
            field="enableAutomaticDeletionOfCompletionItems"
            label={messages.achievementClearCompletionItemsCheckbox}
          />

          <NumberStepper
            disabled={
              !formState.values?.enableAutomaticDeletionOfCompletionItems
            }
            id="enableAutomaticDeletionOfCompletionItemsMonths"
            field="enableAutomaticDeletionOfCompletionItemsMonths"
            label={messages.achievementClearCompletionItemsTimeInterval}
            min={1}
            max={120} // max of 10 years
            stepValue={1}
            style={{ marginTop: 10 }}
          />
        </FormSection>
      ) : null}
    </>
  );
};

export default AchievementSettingsDetails;
